import { useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, EditableToken } from '@vibesites/runtime/react';

const SIGHTMAP_API_SRC = 'https://sightmap.com/embed/api.js';
const DEFAULT_EMBED_SRC =
  'https://sightmap.com/embed/dqw98k95po9?enable_api=1&origin=https://www.liveattheotis.com/';

// Minimal shape of the third-party globals this embed relies on.
interface SightMapEmbedInstance {
  on(event: string, handler: (event: unknown) => void): void;
}
interface SightMapGlobal {
  Embed: new (id: string) => SightMapEmbedInstance;
}
interface EliseAiGlobal {
  onOpenEmailUsWindow?: () => void;
  onOpenSST?: () => void;
}

/**
 * Hardcoded Sightmap interactive map embed for The Otis.
 *
 * Loads the Sightmap embed API, mounts the iframe, and wires the outbound
 * contact/schedule click events into the EliseAi widget — replacing the
 * previous `block:engrain-sitemap` block section.
 */
export function SightMapEmbed() {
  // Default resolves from the static contentSchema defaultValue below.
  const embedSrc = useContentToken('sightmapembed.embed_url') || DEFAULT_EMBED_SRC;

  const initialisedRef = useRef(false);

  useEffect(() => {
    if (initialisedRef.current) return;

    const win = window as unknown as {
      SightMap?: SightMapGlobal;
      eliseAi?: EliseAiGlobal;
    };

    const initEmbed = () => {
      if (initialisedRef.current || !win.SightMap) return;
      initialisedRef.current = true;

      const embed = new win.SightMap.Embed('sightmap');
      embed.on('outbound.contact.click', () => {
        win.eliseAi?.onOpenEmailUsWindow?.();
      });
      embed.on('outbound.schedule.click', () => {
        win.eliseAi?.onOpenSST?.();
      });
    };

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${SIGHTMAP_API_SRC}"]`
    );

    if (win.SightMap) {
      initEmbed();
      return;
    }

    if (!script) {
      script = document.createElement('script');
      script.src = SIGHTMAP_API_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    script.addEventListener('load', initEmbed);
    return () => {
      script?.removeEventListener('load', initEmbed);
    };
  }, []);

  return (
    <section className="bg-[var(--vibe-color-background)] pb-10 md:pb-14">
      <div id="ipm-embed-container" className="w-full">
        <iframe
          id="sightmap"
          title="The Otis interactive property map"
          src={embedSrc}
          width={2000}
          height={980}
          frameBorder="0"
          allow="geolocation"
          className="block w-full max-w-full"
        />
      </div>

      {/* Schema-lint anchor for static analysis — hidden at runtime */}
      <EditableToken
        path="sightmapembed.embed_url"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {embedSrc}
      </EditableToken>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SightMapEmbed',
  displayName: 'SightMap Interactive Map',
  tokens: {
    'sightmapembed.embed_url': {
      label: 'SightMap Embed URL',
      type: 'text',
      defaultValue:
        'https://sightmap.com/embed/dqw98k95po9?enable_api=1&origin=https://www.liveattheotis.com/',
    },
  },
};

(SightMapEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SightMapEmbed;
